import React, { useState, useEffect } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Copy, Download, Loader2, RefreshCcw } from "lucide-react";

/* ---------------- AccessCodesView ----------------
 * Admin view: create partner codes, see usage counts, drill in to which
 * accounts joined via each code, and download the partner-tutorial PDF
 * Ashlee can hand any new clinic. The whole thing is intentionally one
 * compact card stack — partners are a low-volume, high-context surface.
 */
export default function AccessCodesView() {
    const [codes, setCodes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [creating, setCreating] = useState(false);
    const [form, setForm] = useState({
        partner_name: "",
        partner_email: "",
        partner_org: "",
        notes: "",
        max_uses: "",
        code: "",
    });
    const [openCode, setOpenCode] = useState(null);   // code currently expanded for drill-down
    const [codeUsers, setCodeUsers] = useState({});   // { code: [users] }

    const load = async () => {
        setLoading(true);
        try {
            const r = await api.get("/admin/access-codes");
            setCodes(r.data?.codes || []);
        } catch {
            toast.error("Couldn't load access codes.");
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => { load(); }, []);

    const create = async (e) => {
        e.preventDefault();
        if (!form.partner_name.trim()) { toast.error("Give the partner a name."); return; }
        setCreating(true);
        try {
            const body = {
                partner_name: form.partner_name.trim(),
                partner_email: form.partner_email.trim() || null,
                partner_org: form.partner_org.trim(),
                notes: form.notes.trim(),
                max_uses: form.max_uses ? parseInt(form.max_uses, 10) : null,
                code: form.code.trim().toUpperCase() || "",
            };
            await api.post("/admin/access-codes", body);
            toast.success("Access code created.");
            setForm({ partner_name: "", partner_email: "", partner_org: "", notes: "", max_uses: "", code: "" });
            load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Couldn't create the code.");
        } finally {
            setCreating(false);
        }
    };

    const toggleActive = async (code, next) => {
        try {
            await api.patch(`/admin/access-codes/${code}`, { is_active: next });
            setCodes(cs => cs.map(c => c.code === code ? { ...c, is_active: next } : c));
            toast.success(next ? "Code reactivated." : "Code deactivated.");
        } catch {
            toast.error("Couldn't update the code.");
        }
    };

    const drillIn = async (code) => {
        if (openCode === code) {
            setOpenCode(null);
            return;
        }
        setOpenCode(code);
        if (!codeUsers[code]) {
            try {
                const r = await api.get(`/admin/access-codes/${code}/users`);
                setCodeUsers(u => ({ ...u, [code]: r.data?.users || [] }));
            } catch {
                toast.error("Couldn't load users for this code.");
            }
        }
    };

    const downloadTutorial = async () => {
        try {
            const res = await api.get("/admin/partner-tutorial.pdf", { responseType: "blob" });
            const blob = new Blob([res.data], { type: "application/pdf" });
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = "memory-lane-partner-tutorial.pdf";
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(url);
            toast.success("Partner tutorial downloaded.");
        } catch {
            toast.error("Couldn't download the tutorial.");
        }
    };

    const copyCode = async (code) => {
        try {
            await navigator.clipboard.writeText(code);
            toast.success(`${code} copied.`);
        } catch {
            toast.error("Copy failed.");
        }
    };

    return (
        <section className="space-y-6" data-testid="access-codes-view">
            <header>
                <h2 className="font-heading text-2xl text-[#12776F]">Partner access codes</h2>
                <p className="text-sm text-[#4C5551] mt-1 max-w-2xl">
                    Each partner (clinic, hospice, hospital, health system) gets a short reusable code.
                    Caregivers redeem it on the login page when creating their account, and we tally
                    accounts per partner here.
                </p>
            </header>

            {/* Create + tutorial download */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                <form
                    onSubmit={create}
                    className="lg:col-span-2 ml-card p-5 md:p-6 bg-white space-y-3"
                    data-testid="access-codes-create-form"
                >
                    <p className="text-xs uppercase tracking-wider font-semibold text-teal-dark">Create a code</p>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <input
                            type="text"
                            value={form.partner_name}
                            onChange={(e) => setForm({ ...form, partner_name: e.target.value })}
                            placeholder="Partner name (required)"
                            data-testid="access-codes-input-partner-name"
                            className="ml-input"
                        />
                        <input
                            type="text"
                            value={form.partner_org}
                            onChange={(e) => setForm({ ...form, partner_org: e.target.value })}
                            placeholder="Health system / org (optional)"
                            data-testid="access-codes-input-partner-org"
                            className="ml-input"
                        />
                        <input
                            type="email"
                            value={form.partner_email}
                            onChange={(e) => setForm({ ...form, partner_email: e.target.value })}
                            placeholder="Partner contact email (optional)"
                            data-testid="access-codes-input-partner-email"
                            className="ml-input"
                        />
                        <input
                            type="number"
                            min="1"
                            value={form.max_uses}
                            onChange={(e) => setForm({ ...form, max_uses: e.target.value })}
                            placeholder="Max uses (blank = unlimited)"
                            data-testid="access-codes-input-max-uses"
                            className="ml-input"
                        />
                        <input
                            type="text"
                            value={form.code}
                            onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })}
                            placeholder="Vanity code (optional, e.g. CLEVELAND)"
                            data-testid="access-codes-input-vanity"
                            className="ml-input font-mono"
                            maxLength={32}
                        />
                        <input
                            type="text"
                            value={form.notes}
                            onChange={(e) => setForm({ ...form, notes: e.target.value })}
                            placeholder="Internal notes"
                            data-testid="access-codes-input-notes"
                            className="ml-input"
                        />
                    </div>
                    <button
                        type="submit"
                        disabled={creating}
                        data-testid="access-codes-create-submit"
                        className="ml-btn-primary inline-flex items-center gap-2 disabled:opacity-50"
                    >
                        {creating ? "Creating…" : "Create access code"}
                    </button>
                </form>

                <div className="ml-card p-5 md:p-6 bg-teal-soft/40 !border-teal-light flex flex-col gap-3" data-testid="access-codes-tutorial-card">
                    <p className="text-xs uppercase tracking-wider font-semibold text-teal-dark">Partner kit</p>
                    <h3 className="font-heading text-lg text-[#1F2624]">Partner onboarding PDF</h3>
                    <p className="text-sm text-[#4C5551] leading-relaxed">
                        Hand this PDF to every new clinic, hospice or health system. It explains
                        how their patients enter the access code and create a Memory Lane account.
                    </p>
                    <button
                        type="button"
                        onClick={downloadTutorial}
                        data-testid="access-codes-download-tutorial"
                        className="ml-btn-primary inline-flex items-center justify-center gap-2 mt-auto"
                    >
                        <Download size={14} strokeWidth={1.8} />
                        Download tutorial PDF
                    </button>
                </div>
            </div>

            {/* Code list */}
            <div className="ml-card p-0 bg-white overflow-hidden" data-testid="access-codes-list-card">
                <div className="flex items-center justify-between px-5 py-3 border-b border-cream-dark">
                    <p className="text-xs uppercase tracking-wider font-semibold text-[#6E7A74]">All codes</p>
                    <button
                        type="button"
                        onClick={load}
                        data-testid="access-codes-refresh"
                        className="text-xs font-medium text-teal-dark inline-flex items-center gap-1 hover:underline"
                    >
                        <RefreshCcw size={12} /> Refresh
                    </button>
                </div>
                {loading ? (
                    <div className="p-8 text-center text-sm text-[#6E7A74] inline-flex items-center gap-2 justify-center w-full">
                        <Loader2 size={14} className="animate-spin" /> Loading…
                    </div>
                ) : codes.length === 0 ? (
                    <div className="p-8 text-center text-sm text-[#6E7A74]" data-testid="access-codes-empty">
                        No access codes yet. Create the first one above.
                    </div>
                ) : (
                    <ul className="divide-y divide-cream-dark" data-testid="access-codes-list">
                        {codes.map(c => (
                            <li key={c.code} data-testid={`access-code-row-${c.code}`} className="px-5 py-4">
                                <div className="flex flex-wrap items-start gap-3 justify-between">
                                    <div className="min-w-0">
                                        <p className="flex items-center gap-2 mb-1">
                                            <button
                                                type="button"
                                                onClick={() => copyCode(c.code)}
                                                data-testid={`access-code-copy-${c.code}`}
                                                className="font-mono text-base font-semibold text-teal-dark hover:underline"
                                                title="Click to copy"
                                            >
                                                {c.code}
                                            </button>
                                            {!c.is_active && (
                                                <span className="text-[10px] uppercase font-semibold px-2 py-0.5 rounded-2xl bg-yellow-light text-yellow-dark border border-yellow">
                                                    inactive
                                                </span>
                                            )}
                                        </p>
                                        <p className="text-sm text-[#1F2624]">
                                            <strong>{c.partner_name}</strong>
                                            {c.partner_org && <span className="text-[#6E7A74]"> · {c.partner_org}</span>}
                                        </p>
                                        {c.partner_email && (
                                            <p className="text-xs text-[#6E7A74]">{c.partner_email}</p>
                                        )}
                                        {c.notes && (
                                            <p className="text-xs text-[#4C5551] italic mt-1">&ldquo;{c.notes}&rdquo;</p>
                                        )}
                                    </div>
                                    <div className="text-right shrink-0">
                                        <p className="text-2xl font-heading text-[#12776F]" data-testid={`access-code-count-${c.code}`}>
                                            {c.used_count}
                                            <span className="text-base text-[#6E7A74] font-normal">
                                                {typeof c.max_uses === "number" ? ` / ${c.max_uses}` : ""}
                                            </span>
                                        </p>
                                        <p className="text-[10px] uppercase tracking-wider font-semibold text-[#6E7A74]">
                                            accounts created
                                        </p>
                                        {c.latest_user && (
                                            <p className="text-xs text-[#4C5551] mt-1">
                                                last: {String(c.latest_user.created_at || "").slice(0, 10)}
                                            </p>
                                        )}
                                    </div>
                                </div>
                                <div className="flex flex-wrap gap-2 mt-3">
                                    <button
                                        type="button"
                                        onClick={() => drillIn(c.code)}
                                        data-testid={`access-code-drill-${c.code}`}
                                        className="text-xs font-medium px-3 py-1.5 rounded-2xl bg-cream-light border border-cream-dark text-[#1F2624] hover:bg-cream-dark/30"
                                    >
                                        {openCode === c.code ? "Hide accounts" : "View accounts"}
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => toggleActive(c.code, !c.is_active)}
                                        data-testid={`access-code-toggle-${c.code}`}
                                        className="text-xs font-medium px-3 py-1.5 rounded-2xl bg-white border border-cream-dark text-[#1F2624] hover:bg-cream-dark/30"
                                    >
                                        {c.is_active ? "Deactivate" : "Reactivate"}
                                    </button>
                                </div>
                                {openCode === c.code && (
                                    <ul className="mt-3 space-y-1 rounded-2xl bg-cream-light border border-cream-dark p-3" data-testid={`access-code-users-${c.code}`}>
                                        {(codeUsers[c.code] || []).length === 0 ? (
                                            <li className="text-xs text-[#6E7A74]">No accounts yet.</li>
                                        ) : (
                                            (codeUsers[c.code] || []).map((u) => (
                                                <li key={u.email} className="text-sm text-[#1F2624] flex justify-between gap-3">
                                                    <span>
                                                        <strong>{u.name || u.email}</strong>
                                                        <span className="text-[#6E7A74]"> · {u.email}</span>
                                                    </span>
                                                    <span className="text-xs text-[#6E7A74]">
                                                        {String(u.created_at || "").slice(0, 10)}
                                                    </span>
                                                </li>
                                            ))
                                        )}
                                    </ul>
                                )}
                            </li>
                        ))}
                    </ul>
                )}
            </div>
        </section>
    );
}
